/**
 * ECPage — `/pulse/ec`.
 *
 * Detail screen for the EC (Soil Nutrients) card. Surfaces:
 *   • Live EC (µS/cm) + status
 *   • 48h trend graph w/ 400-700 ideal range band
 *   • 24h trend / change tiles
 *   • Insight tied to current EC vs flush threshold
 *   • Recommendation (do/don't fertilize)
 */
import React, { useEffect, useState } from 'react';
import { Leaf, AlertTriangle, Lightbulb, Brain, Users } from 'lucide-react';
import { PulseDetailLayout, PulseTile, PulseInsight } from '../../components/pulse/PulseDetailLayout';
import { PulseSparkline } from '../../components/pulse/PulseSparkline';
import { PerChannelSparkline } from '../../components/pulse/PerChannelSparkline';
import { API_URL } from '../../config/api';

function statusFor(v) {
  if (v == null) return { text: '—', color: 'text-slate-400' };
  if (v < 400)  return { text: 'Low',      color: 'text-amber-400' };
  if (v < 700)  return { text: 'Ideal',    color: 'text-emerald-400' };
  if (v < 900)  return { text: 'Elevated', color: 'text-amber-400' };
  if (v < 1500) return { text: 'High',     color: 'text-amber-500' };
  return { text: 'Critical', color: 'text-rose-500' };
}

export default function ECPage() {
  const [now, setNow] = useState(null);
  const [hist, setHist] = useState(null);
  const [perProbe, setPerProbe] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/dashboard/now`, { credentials: 'include' })
      .then(r => r.json()).then(setNow).catch(() => {});
    fetch(`${API_URL}/api/dashboard/history?metric=ec&hours=48`, { credentials: 'include' })
      .then(r => r.json()).then(setHist).catch(() => {});
  }, []);

  const value = now?.metrics?.ec?.value;
  const st = statusFor(value);
  const delta = hist?.delta;
  const trend = hist?.trend;

  return (
    <PulseDetailLayout
      title="EC (Soil)"
      icon={Leaf}
      iconColor="text-emerald-400"
      bigValue={value != null ? Math.round(value) : null}
      bigValueColor="text-emerald-400"
      bigValueUnit=""
      badgeText={st.text}
      badgeColor={st.color}
    >
      <div className="flex items-center justify-between pl-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
          Trend · 48h · µS/cm
          {now?.metrics?.ec?.probe_count > 1 && (
            <span className="ml-2 normal-case tracking-normal text-slate-500"
                  data-testid="ec-probe-context">
              avg of {now.metrics.ec.probe_count} probes
              {now.metrics.ec.range && (
                <> · {now.metrics.ec.range.min}–{now.metrics.ec.range.max} µS/cm</>
              )}
            </span>
          )}
        </div>
        {/* Per-probe toggle — flips the sparkline from a single averaged
            line to per-channel overlay so the user can spot outliers. Only
            shown when there's more than one probe to compare. */}
        {now?.metrics?.ec?.probe_count > 1 && (
          <button
            data-testid="ec-per-probe-toggle"
            onClick={() => setPerProbe(v => !v)}
            className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
          >
            <Users className="w-3 h-3" />
            {perProbe ? 'Average' : 'Per probe'}
          </button>
        )}
      </div>
      {perProbe
        ? <PerChannelSparkline metric="ec" hours={48} unit="" idealMin={400} idealMax={700} height={240} />
        : <PulseSparkline metric="ec" hours={48} idealMin={400} idealMax={700} unit="" />}

      <div className="grid grid-cols-2 gap-3">
        <PulseTile
          testid="ec-ideal-range-tile"
          label="Ideal Range"
          value="400 – 700"
          valueColor="text-emerald-400"
          subline="µS/cm"
        />
        <PulseTile
          testid="ec-change-tile"
          label="Change (24h)"
          value={delta != null
            ? `${delta > 0 ? '↑' : delta < 0 ? '↓' : '·'} ${Math.abs(Math.round(delta))}`
            : '—'}
          valueColor={trend === 'falling' ? 'text-emerald-400'
                      : trend === 'rising' ? 'text-amber-400'
                      : 'text-slate-100'}
          subline={trend ? trend.charAt(0).toUpperCase() + trend.slice(1) : '—'}
        />
      </div>

      <PulseInsight tone="emerald" icon={Brain} label="Insight" testid="ec-insight">
        {value == null
          ? 'Add an EC-capable soil sensor (e.g., Ecowitt WH51X) to unlock nutrient intelligence.'
          : value < 400
            ? 'EC is low — soil has reset, ready for a fresh fertilizer window.'
            : value < 700
              ? 'Soil EC is in ideal range. Fertilizer is being processed effectively.'
              : 'Soil EC is elevated. Fertilizer is still being processed by the soil.'}
      </PulseInsight>

      {value != null && value >= 900 && (
        <PulseInsight tone="amber" icon={AlertTriangle} label="Caution" testid="ec-caution">
          Adding nutrients now can reduce efficiency and increase waste — the soil hasn't
          finished metabolizing the last application.
        </PulseInsight>
      )}

      <PulseInsight tone="emerald" icon={Lightbulb} label="Recommendation" testid="ec-recommendation">
        {value == null
          ? 'Waiting on EC sensor reading.'
          : value >= 1500
            ? 'Flush the profile with 1.0" of clean water. EC has crossed the critical band.'
            : value >= 900
              ? 'Do NOT fertilize yet. Let levels return to the 400–700 ideal range.'
              : value < 400
                ? 'Apply a light foliar feed at next IDEAL VPD window — soil tank is empty.'
                : 'Hold steady. Nutrient delivery is on track.'}
      </PulseInsight>
    </PulseDetailLayout>
  );
}
